import { motion } from "framer-motion";
import {
  HeartHandshake,
  IndianRupee,
  ShieldCheck,
  Sparkles,
  Wrench,
  PhoneCall,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const TIMELINE = [
  { year: "2011", text: "Started as a two-technician team servicing local Kukatpally homes." },
  { year: "2015", text: "Expanded coverage across Hyderabad and became multi-brand." },
  { year: "2019", text: "Crossed 2,500 serviced homes across the twin states." },
  { year: "2023", text: "Launched annual maintenance plans to make service predictable and affordable." },
  { year: "2026", text: "Went digital — one Customer ID, all plans, all service history in your pocket." },
];

const REASONS = [
  {
    icon: HeartHandshake,
    t: "Single window, every brand",
    d: "Whether it's a 15-year-old fridge or a brand-new inverter AC — one call, one team, one bill. No more chasing brand service centres across the city.",
  },
  {
    icon: IndianRupee,
    t: "Affordable, transparent pricing",
    d: "Plans from just ₹799/year and flat-rate repairs from ₹350. What you see is what you pay — no hidden inspection fees, no surprise charges.",
  },
  {
    icon: ShieldCheck,
    t: "Warranty you can hold us to",
    d: "Every spare and gas charging is covered by a written 30 to 120-day warranty depending on your plan. If it fails, we fix it — free.",
  },
  {
    icon: Sparkles,
    t: "Certified, OEM-trained technicians",
    d: "Our engineers are trained across major manufacturers and carry genuine parts. No guesswork, no imitation spares.",
  },
  {
    icon: Wrench,
    t: "Under-one-umbrella services",
    d: "Maintenance, repair, installation, deep cleaning, gas charging, chemical wash — all handled by the same team on the same visit if needed.",
  },
  {
    icon: PhoneCall,
    t: "Save your time and your money",
    d: "One phone number for nine appliances. Digital service history in your Customer ID. Fewer calls, fewer visits, fewer bills. That's the whole point.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero + story (dark) */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-20">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            About ASA · Hyderabad
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.4rem] tracking-tight text-white leading-[1.08] max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            15 years of fixing homes across the{" "}
            <span className="text-[#F26A21]">Telugu states.</span>
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
            <motion.div
              className="lg:col-span-7 space-y-5 text-[#AEBECD] text-lg leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <p>
                ASA Home Appliances Services was born inside real Hyderabad homes
                — one broken fridge, one leaking washing machine, one silent AC
                at a time. Fifteen years and thousands of service calls later,
                we're still doing the same thing, just for a lot more families.
              </p>
              <p>
                For over 15 years, we've serviced home appliances across
                Telangana and Andhra Pradesh — from budget single-door fridges in
                a rented flat to full modular kitchens in gated communities. Over
                the years we noticed something: every family had the same
                problem, just wearing different clothes.
              </p>
              <p>
                Ten appliances, ten different brand call-centres. A visit fee just
                to be told the part would take a week. Bills that changed between
                the estimate and the invoice. Warranty cards that mysteriously
                "expired" the day before something broke. It was fragmented,
                expensive and honestly, tiring.
              </p>
              <p>
                So we did what we've always done — we fixed it. ASA is a
                single-window service company for every appliance in your home,
                across every brand, at a price that doesn't feel like a
                punishment. One team. One bill. One Customer ID that tracks every
                visit forever.
              </p>
              <p>
                We built our plans so a family could get more service, faster, and
                for less than they used to spend on one out-of-warranty repair.
                That's the whole promise.
              </p>
            </motion.div>

            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              {[
                ["15+", "Years serving homes"],
                ["2", "Telugu states covered"],
                ["9", "Appliance categories"],
              ].map(([v, l], i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="rounded-2xl bg-[#122539] border border-[#223851] p-6">
                    <p className="font-heading font-extrabold text-4xl text-white">{v}</p>
                    <p className="overline text-[#6B7E92] mt-2">{l}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-6 border-y border-[#1B3350] bg-[#0A1626]">
        <Marquee items={["ONE TEAM", "ONE BILL", "ONE CUSTOMER ID", "EVERY BRAND"]} />
      </section>

      {/* Why families choose ASA (LIGHT) */}
      <section className="bg-[#EEF1F5] py-24 md:py-32">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="overline text-[#8A97A6] mb-4">Why families choose ASA</p>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#0C1C2E]">
                Six things you'll notice on day one.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {REASONS.map((r, i) => (
              <Reveal key={r.t} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`reason-card-${i + 1}`}
                  className="h-full rounded-3xl bg-white border border-[#E1E6EC] p-8 shadow-[0_1px_2px_rgba(12,28,46,0.04)] hover:shadow-[0_12px_40px_rgba(12,28,46,0.08)] transition-shadow duration-300"
                >
                  <span className="w-12 h-12 rounded-full bg-[#F26A21]/12 flex items-center justify-center">
                    <r.icon size={22} className="text-[#F26A21]" />
                  </span>
                  <h3 className="font-heading font-bold text-xl text-[#0C1C2E] mt-6">{r.t}</h3>
                  <p className="text-[#5B6B7B] mt-3 leading-relaxed">{r.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The Journey timeline (LIGHT) */}
      <section className="bg-[#F7F9FB] py-24 md:py-32 border-t border-[#E1E6EC]">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="overline text-[#8A97A6] mb-4">The journey</p>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#0C1C2E]">
                From a two-person team to a Telugu-states network.
              </h2>
            </div>
          </Reveal>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-2 bottom-2 w-px bg-[#DCE2E9] -translate-x-1/2" />
            {TIMELINE.map((it, i) => {
              const left = i % 2 === 0;
              return (
                <Reveal key={it.year} delay={0.05}>
                  <div className="relative md:grid md:grid-cols-2 md:gap-14 mb-6 md:mb-2">
                    <span className="hidden md:block absolute left-1/2 top-7 w-3.5 h-3.5 rounded-full bg-[#F26A21] ring-4 ring-[#F26A21]/20 -translate-x-1/2 z-10" />
                    <div className={left ? "md:pr-2" : "md:col-start-2 md:pl-2"}>
                      <div
                        data-testid={`timeline-${it.year}`}
                        className={`rounded-2xl bg-white border border-[#E1E6EC] p-6 shadow-[0_1px_2px_rgba(12,28,46,0.04)] border-l-4 border-l-[#F26A21] md:border-l md:border-l-[#E1E6EC] ${
                          left ? "md:text-right" : "md:text-left"
                        }`}
                      >
                        <p className="font-mono text-sm tracking-widest text-[#8A97A6]">{it.year}</p>
                        <p className="text-[#33475B] mt-2 leading-relaxed">{it.text}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
